import * as React from 'react';

import Select from '../../../../BaseModule/Components/Select/Select';
import UserPickerAdd from './Add/UserPickerAdd';
import UserPickerItem, { UserItem } from './UserPickerItem/UserPickerItem';
import netteApiClient from '../../../../../Services/NetteApiClient';
import { useState } from 'react';
import { useEffectAsync } from '../../../../../helpers';
import { useLazyCaseCreateUsersQuery } from '../../../../../graphql/graphql';
import Loading from '../../../../BaseModule/Components/Loading';

// @ts-ignore
import styles from './UserPicker.module.scss';
import useTranslation from '../../../../../Hooks/useTranslation';
import { getCanAddFamilyMember } from '../../../../User/Services/UserFamily.service';
import { useAppContext } from '../../../../../Services/AppContext';
import Text from '../../../../BaseModule/Components/Text';

interface Props {
	userIds: string[];
	selectedUserId?: string;
	linkFamilyMemberCreate: string;
	linkFamilyMemberSelect?: string;
	onSelect?: (userId: string) => void;
	label: string;
	addAvailable: boolean;
	childOnly?: boolean;
	isInvalid?: boolean;
	showNote?: boolean;
}

const UserPicker: React.FC<Props> = ({
	userIds,
	selectedUserId,
	linkFamilyMemberCreate,
	linkFamilyMemberSelect,
	onSelect,
	label,
	addAvailable,
	childOnly = false,
	isInvalid = false,
	showNote = false,
}) => {
	const [users, setUsers] = useState<UserItem[] | undefined>(undefined);
	const [getUsers] = useLazyCaseCreateUsersQuery();
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const { userData } = useAppContext();
	const [canAddMember, setCanAddMember] = useState(false);

	const onUserAdd = async () => {
		await netteApiClient.get(linkFamilyMemberCreate);
	};

	const onUserSelect = async (userId: string) => {
		if (linkFamilyMemberSelect) {
			await netteApiClient.get(
				linkFamilyMemberSelect.replace('__ID__', userId),
			);
		} else {
			onSelect?.(userId);
		}
	};

	useEffectAsync(async () => {
		setCanAddMember(await getCanAddFamilyMember(userData.id));
	}, []);

	useEffectAsync(async () => {
		if (!users) {
			setIsLoading(true);
			const { users: data } = await getUsers({
				userIds,
			}).unwrap();

			const tmpUsers = [];
			if (data?.data?.length) {
				for (const user of data.data) {
					if (childOnly && !user?.isChild) {
						continue;
					}
					if (user) {
						tmpUsers.push({
							id: user.id,
							firstName: user.profile?.firstName || '',
							lastName: user.profile?.lastName || '',
							fullName: user.profile?.fullName || '',
							profileImageUrl: user.profileImageUrl || null,
						});
					}
				}
				tmpUsers.sort((a, b) => {
					return userIds.indexOf(a.id) - userIds.indexOf(b.id);
				});
			}
			if (
				childOnly &&
				tmpUsers &&
				(tmpUsers?.length || 0) > 0 &&
				!tmpUsers?.some(item => item.id === selectedUserId)
			) {
				onUserSelect(tmpUsers[0]?.id);
			}
			setUsers(tmpUsers);
			setIsLoading(false);
		}
	}, []);

	const showSelect = (users?.length || 0) > 1;

	if (isLoading || !users) {
		return (
			<div className={styles.wrapper}>
				<div className={styles.loading}>
					<Loading />
				</div>
			</div>
		);
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.userPicker}>
				{users?.map(user => (
					<UserPickerItem
						key={user.id}
						user={user}
						onClick={onUserSelect}
						isSelected={user.id === selectedUserId}
					></UserPickerItem>
				))}
				{canAddMember && <UserPickerAdd />}
			</div>
			{showSelect && (
				<div className={styles.formCard}>
					<div className={styles.formGroup}>
						<div className={styles.label}>
							<div className={styles.text}>{t(label)}</div>
						</div>
						<div className={styles.select}>
							<Select
								options={users?.map(user => ({
									value: user.id,
									label: user.fullName,
								}))}
								value={selectedUserId}
								onChange={onUserSelect}
								returnKey={'value'}
								placeholder='component.userPicker.pickPerson'
								error={
									isInvalid
										? {
												type: '',
												message: t('component.userPicker.youMustPickPerson'),
										  }
										: undefined
								}
							/>
						</div>
						{showNote && (
							<div className={styles.label}>
								<div className={styles.text}>
									{t('component.userPicker.note')}
								</div>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default UserPicker;
